import { getPrimaryColor, JEJURO } from '@orcar/common-d2c';
import styled, { css } from 'styled-components';

const COMPANY_NAME = '제주로 렌트카 | 갤럭시 렌터카';
const ADDRESS = '제주특별자치도 제주시 오일장중길 128 (도두이동)';
const OPERATING_HOURS = '매일 08:00 ~ 22:00';

/** 지도에서 보기 / 길찾기 링크 (제주로 렌트카 위치) */
const MAP_VIEW_URL = 'https://maps.app.goo.gl/XXfxqBgJeofYSwDs9';
const MAP_DIRECTION_URL = 'https://maps.app.goo.gl/3gYBN4oBxEkvVE6F8';

// API 키 없이 임베드되는 구글맵(장소 검색 결과 중심).
const MAP_EMBED_URL = `https://maps.google.com/maps?q=${encodeURIComponent(
  '제주로렌트카 제주시 오일장중길 128',
)}&z=15&hl=ko&output=embed`;

type HomeLocationSectionProps = {
  isMobile: boolean;
};

/** 홈 하단 "오시는 길" 섹션 (지도 + 주소·영업시간 + 지도보기/길찾기). */
const HomeLocationSection = ({ isMobile }: HomeLocationSectionProps) => {
  return (
    <Section isMobile={isMobile}>
      <Inner>
        <Title isMobile={isMobile}>오시는 길</Title>
        <MapWrap isMobile={isMobile}>
          <iframe
            title='제주로 렌트카 위치'
            src={MAP_EMBED_URL}
            loading='lazy'
            referrerPolicy='no-referrer-when-downgrade'
          />
        </MapWrap>
        <InfoRow isMobile={isMobile}>
          <Detail>
            <Name>{COMPANY_NAME}</Name>
            <AddressText>{ADDRESS}</AddressText>
            <Hours>운영시간 : {OPERATING_HOURS}</Hours>
          </Detail>
          <Buttons isMobile={isMobile}>
            <OutlineButton
              href={MAP_VIEW_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              지도에서 보기
            </OutlineButton>
            <PrimaryButton
              href={MAP_DIRECTION_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              길 찾기
            </PrimaryButton>
          </Buttons>
        </InfoRow>
      </Inner>
    </Section>
  );
};

export default HomeLocationSection;

const Section = styled.section<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: ${({ isMobile }) => (isMobile ? '40px 0' : '80px 0')};
`;

const Inner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.WIDTH.PC_LAYOUT};
  padding: 0 20px;
`;

const Title = styled.h2<{ isMobile: boolean }>`
  margin: 0 0 24px;
  ${({ isMobile, theme }) => (isMobile ? theme.FONT.H3 : theme.FONT.H2)};
  font-weight: 800;
  letter-spacing: -0.03em;
  text-align: center;
`;

const MapWrap = styled.div<{ isMobile: boolean }>`
  width: 100%;
  height: ${({ isMobile }) => (isMobile ? '240px' : '380px')};
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const InfoRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;

  ${({ isMobile }) =>
    isMobile &&
    css`
      flex-direction: column;
      align-items: stretch;
    `}
`;

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Name = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.H4};
  font-weight: 800;
`;

const AddressText = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B1};
  color: ${({ theme }) => theme.COLOR.GREY_01};
`;

const Hours = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const Buttons = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: 10px;
  flex: 0 0 auto;

  ${({ isMobile }) =>
    isMobile &&
    css`
      width: 100%;
      a {
        flex: 1;
      }
    `}
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 24px;
  border-radius: 12px;
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: filter 0.15s ease;
`;

const OutlineButton = styled.a`
  ${buttonBase};
  color: ${({ theme }) => theme.COLOR.GREY_01};
  background: ${({ theme }) => theme.COLOR.WHITE};
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_04};

  &:hover {
    background: ${({ theme }) => theme.COLOR.GREY_BG};
  }
`;

const PrimaryButton = styled.a`
  ${buttonBase};
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};

  &:hover {
    filter: brightness(0.96);
  }
`;
